import React, { useState, useEffect } from 'react';
import propTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import { format, parse, isAfter, endOfDay, startOfDay } from 'date-fns';
import classnames from 'classnames';

import ConfirmDialog from '../../../../../../../../components/ConfirmDialog';
import { getEquipmentColorClass } from 'helpers/_helpers';
import { hasPermissionsFor } from 'helpers/_helpers';
import styles from './styles';
import CloseIconGrey from 'assets/images/close-icon-grey.svg';
import { getEquipmentEvents } from 'store/availability/availabilityPagesOperations';
import { getOptions } from 'helpers/getOptions';
import $api from 'http/index';
import { useDispatch } from 'react-redux';
import { globalBEDateFormat } from 'common/dateFormatConfig';

const EquipmentListForADay = ({
  classes,
  event,
  isNotCurrentMonth,
  openAssignModal,
  isInPast,
  openSnackbar,
}) => {
  const dispatch = useDispatch();
  const [isHidden, setIsHidden] = useState(true);
  const [modal, setModal] = useState({
    isOpen: false,
    eventId: '',
  });

  const ref = React.createRef();

  useEffect(() => {
    document.addEventListener('click', hideList, true);
    return () => {
      document.removeEventListener('click', hideList, true);
    };
  }, []);

  const toggleListLength = (e) => {
    e.stopPropagation();
    setIsHidden((prev) => !prev);
  };

  const hideList = (event) => {
    if (ref.current && !ref.current.contains(event.target) && !isHidden && !modal.isOpen) {
      setIsHidden(true);
    }
  };

  const getEquipmentInfo = (equipment, field) => {
    if (!equipment) return '';
    if (field === 'number') {
      return parseInt(equipment.name);
    }
    if (field === 'className') {
      return getEquipmentColorClass(equipment.color);
    }
    return equipment[field];
  };

  const getDayNumber = (date) => format(parse(date, globalBEDateFormat, new Date()), 'dd');

  const isToday = (date) => format(new Date(), globalBEDateFormat) === date;

  const handleDeleteEquipmentEvent = (eventId) => (e) => {
    e.stopPropagation();
    setModal({ isOpen: true, eventId });
  };

  const deleteEquipmentEvent = async () => {
    const { eventId } = modal;

    try {
      await $api.delete(
        `${process.env.REACT_APP_BASE_URL}/calendar-events/${eventId}`,
        getOptions()
      );
      dispatch(getEquipmentEvents());
      openSnackbar('success', 'Successfully Deleted!');
    } catch (error) {
      openSnackbar('error', error.message);
    }
    closeModal();
  };

  const isEditableEvent = (start) => {
    const startDate = endOfDay(parse(start, globalBEDateFormat, new Date()));
    const todayStartTime = startOfDay(new Date());
    return isAfter(startDate, todayStartTime);
  };

  const hasAccess = () => {
    return (
      hasPermissionsFor('availabilityEquipmentEdit') || hasPermissionsFor('availabilityFullAccess')
    );
  };
  const closeModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
    setIsHidden(true);
  };

  return (
    <>
      <div
        className={classnames(
          classes.cellContent,
          !isHidden && classes.absolute,
          !isHidden && isInPast(event.dayNumber) && classes.greyBackground
        )}
        onBlur={hideList}
        ref={ref}
        onClick={
          isInPast(event.dayNumber)
            ? null
            : hasAccess()
            ? openAssignModal('equipment', event)
            : null
        }
      >
        <div>
          <span
            className={classnames(
              classes.dayNumber,
              isToday(event.dayNumber) && classes.todayNumber
            )}
          >
            {getDayNumber(event.dayNumber)}
          </span>
        </div>

        <div className={classnames(classes.list, !isHidden && classes.visible)}>
          {event.events.map((calendarEvent) => (
            <div key={calendarEvent._id} className={classes.flexBox}>
              <div
                className={classnames(
                  classes.equipmentBlock,
                  getEquipmentInfo(calendarEvent.equipment, 'className'),
                  !isEditableEvent(calendarEvent.start) && classes.fullWidth
                )}
                style={{
                  backgroundColor: getEquipmentInfo(calendarEvent.equipment, 'color'),
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={classes.equipmentNumber}>
                  {getEquipmentInfo(calendarEvent.equipment, 'name')}
                </div>
              </div>
              {isEditableEvent(calendarEvent.start) && hasAccess() && (
                <Button
                  color="secondary"
                  className={classes.actionBtn}
                  onClick={handleDeleteEquipmentEvent(calendarEvent._id)}
                  disableRipple
                >
                  <img src={CloseIconGrey} alt="delete" />
                </Button>
              )}
            </div>
          ))}
        </div>
        {event.events.length > 4 && (
          <div className={classnames(!isHidden && classes.borderBottom)}>
            <Button
              className={classes.othersBtn}
              onClick={toggleListLength}
              variant="text"
              disableRipple
            >
              {isHidden ? `+ ${event.events.length - 4} others` : 'hide'}
            </Button>
          </div>
        )}
      </div>

      {modal.isOpen && (
        <ConfirmDialog
          isOpen={modal.isOpen}
          onClose={closeModal}
          onSubmit={deleteEquipmentEvent}
          text="Are sure you want to delete this calendar event?"
          disableEscape
          loadingOnSubmit
        />
      )}
    </>
  );
};

EquipmentListForADay.propTypes = {
  classes: propTypes.object.isRequired,
  event: propTypes.object.isRequired,
  selectedDate: propTypes.object.isRequired,
  openSnackbar: propTypes.func.isRequired,
  isNotCurrentMonth: propTypes.func.isRequired,
  openAssignModal: propTypes.func.isRequired,
};

export default withStyles(styles)(EquipmentListForADay);
